// ─────────────────────────────────────────────────────────────────────────
// 수업 자료 읽기/쓰기 — 학생앱(강의자·학생)과 콘솔이 함께 쓴다.
//
// Firebase 는 동적 import 로 부른다(licenseService 와 같은 이유 — 초기 번들에 안 얹는다).
// 모델·판정은 lessonPlan.js 에 있고 여기는 저장소만 다룬다.
//
// 규칙 3: 못 읽은 것과 빈 것을 구분한다.
//   읽기 실패 → null (화면은 "불러오지 못했습니다")
//   담긴 게 없음 → emptyPlan() (화면은 "아직 준비 중")
//   이걸 섞으면 통신 오류가 "강의자가 아직 안 담았어요"로 보인다.
// ─────────────────────────────────────────────────────────────────────────
import { ensureAnonAuth } from './anonAuth';
import { assertConsoleWritable } from '../admin/lib/writeGuard';
import { LESSON_PLAN_DOC, normalizePlan } from './lessonPlan';

async function fb() {
  const [{ db, auth }, fs, fbAuth] = await Promise.all([
    import('../admin/firebase/config'),
    import('firebase/firestore'),
    import('firebase/auth'),
  ]);
  if (!db) throw new Error('서버 연결이 구성되지 않았습니다.');
  // 학생·강의자는 익명 로그인 상태로 들어온다(좌석 검증에서 이미 붙는다).
  // 새로고침 등으로 끊겼으면 다시 붙인다 — 규칙이 isSignedIn() 을 요구한다.
  if (auth && !auth.currentUser) {
    // 삼키지 않는다 — 로그인 실패가 아래에서 '권한 없음'으로 둔갑하면
    // 담당자에게는 자료가 통째로 사라진 것처럼 보인다(규칙 3).
    await ensureAnonAuth(auth, fbAuth);
  }
  return { db, fs };
}

function planRef(fs, db, classId) {
  return fs.doc(db, 'classes', classId, 'lessonPlan', LESSON_PLAN_DOC);
}

/**
 * 수업 자료를 실시간으로 본다. 강의자가 담으면 학생 화면에 바로 나타난다
 * (수업 중에 "지금 열었어요" 하면 새로고침 없이 보여야 한다).
 * @returns {() => void} 구독 해제
 */
export function watchLessonPlan(classId, cb) {
  if (!classId || typeof cb !== 'function') return () => {};
  let unsub = null;
  let dead = false;
  (async () => {
    try {
      const { db, fs } = await fb();
      if (dead) return;
      unsub = fs.onSnapshot(
        planRef(fs, db, classId),
        (snap) => cb(snap.exists() ? normalizePlan(snap.data()) : normalizePlan(null)),
        () => cb(null), // 권한·네트워크 실패 — 빈 것이 아니라 "못 읽음"
      );
    } catch {
      if (!dead) cb(null);
    }
  })();
  return () => { dead = true; if (unsub) unsub(); };
}

/**
 * 한 번만 읽는다. 못 읽으면 null.
 * 콘솔이 "마지막으로 담은 게 언제냐"를 말할 수 있게 updatedAt(ms)을 얹어 준다.
 * 화면 컴포넌트는 이 필드를 안 봐도 되고, normalizePlan 을 다시 태우면 자연히 빠진다.
 */
export async function getLessonPlan(classId) {
  if (!classId) return null;
  try {
    const { db, fs } = await fb();
    const snap = await fs.getDoc(planRef(fs, db, classId));
    if (!snap.exists()) return { ...normalizePlan(null), updatedAt: null };
    const raw = snap.data();
    const ts = raw?.updatedAt;
    return { ...normalizePlan(raw), updatedAt: ts?.toMillis ? ts.toMillis() : null };
  } catch {
    return null;
  }
}

/**
 * 강의자가 담은 것을 저장한다.
 * @param {string} classId
 * @param {object} plan            lessonPlan.js 가 만든 모양
 * @param {string} instructorCode  강의자 좌석 코드 — 규칙(isClassInstructor)이 이걸로 검증한다.
 *                                 좌석 문서로 되짚어 확인하므로 아무 문자열이나 통하지 않는다.
 */
export async function saveLessonPlan(classId, plan, instructorCode) {
  if (!classId) throw new Error('반을 알 수 없습니다.');
  if (!instructorCode) throw new Error('강의자 코드가 없습니다.');
  const { db, fs } = await fb();
  const p = normalizePlan(plan);
  await fs.setDoc(planRef(fs, db, classId), {
    picks: p.picks,            // [{ chapterId, week }] — week 는 칸 번호(1-based)
    currentWeek: p.currentWeek,
    // 총 주차도 여기 둔다 — 좌석에만 있으면 이미 들어와 있는 학생에게 반영이 늦는다.
    plannedWeeks: p.plannedWeeks ?? null,
    by: instructorCode,
    updatedAt: fs.serverTimestamp(),
  });
}

/**
 * 담당자(콘솔)가 계획을 고친다 — 총 주차를 미리 맞춰 두는 용도.
 * 규칙은 isInstAdmin 을 따로 허용하므로 강의자 코드(by)가 필요 없다.
 * 강의자가 안 들어와 있을 때 담당자가 대신 맞춰 둘 수 있어야 한다.
 */
export async function saveLessonPlanAsAdmin(classId, plan) {
  if (!classId) throw new Error('반을 알 수 없습니다.');
  // 콘솔 쓰기 잠금 — 운영사가 기관 콘솔을 열람 중이면 담당자 대신 쓰지 않는다.
  // (writeGuard 는 의존성 없는 순수 모듈이라 학생 번들에 부담이 없다.)
  assertConsoleWritable();
  const { db, fs } = await fb();
  const p = normalizePlan(plan);
  await fs.setDoc(planRef(fs, db, classId), {
    picks: p.picks,
    currentWeek: p.currentWeek,
    plannedWeeks: p.plannedWeeks ?? null,
    updatedAt: fs.serverTimestamp(),
  }, { merge: true });
}
